"use client";

import { CSSProperties, JSX } from "react";
import { Image, Text } from "@sitecore-content-sdk/nextjs";
import { SitecoreLink as Link } from "../../../atoms/SitecoreLink";
import { extractHiddenBlock } from "../../../utils/styles";
import { NoDataFallback } from "../../../utils/NoDataFallback";
import { ImageProps } from "components/sxa/image/image.props";

export const Default = (props: ImageProps): JSX.Element => {
  console.log("---->[Image] Component", props?.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields?.Image) {
    return <NoDataFallback componentName="Image" />;
  }

  const id = params?.RenderingIdentifier;
  const styles = params?.Styles ?? "";
  const positionClass = styles.includes("position-left")
    ? "items-start"
    : "items-center";

  const isRoundedVariant = styles.includes(
    "mcclaren-image-with-rounded-corner",
  );
  const isFlatVariant = styles.includes(
    "mcclaren-image-without-rounded-corner",
  );

  const roundedClasses = `rounded-md [&_img]:rounded-md mb-16 flex flex-col ${positionClass}`;

  const flatClasses = `px-4 mb-4 flex flex-col [&_img]:rounded-none! ${positionClass}`;

  let variantClasses = "";

  if (isRoundedVariant) {
    variantClasses = roundedClasses;
  } else if (isFlatVariant) {
    variantClasses = flatClasses;
  }

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  // External links open in a new tab. Build a derived field instead of
  // mutating the incoming props.
  const targetUrl = fields.TargetUrl;
  const isExternalLink = targetUrl?.value?.linktype === "external";
  const targetUrlField =
    isExternalLink && targetUrl?.value
      ? { ...targetUrl, value: { ...targetUrl.value, target: "_blank" } }
      : targetUrl;

  const href = targetUrlField?.value?.href;
  const hasValidLink = !!href && href !== "http://";
  const showLinkWrapper = !isPageEditing && hasValidLink;

  const altText = fields.Image?.value?.alt;
  const isSocialIcon =
    typeof altText === "string" &&
    altText.toLowerCase().includes("social icon");

  const ariaLabelProps = isSocialIcon ? { "aria-label": href ?? "" } : {};

  return (
    <div
      className={`relative overflow-hidden mb-5 ${styles} ${variantClasses} ${hiddenBlockStyles}`}
      id={id || undefined}
      data-role="image-component"
    >
      {showLinkWrapper && targetUrlField ? (
        <Link
          className="inline-block max-w-full cursor-pointer"
          field={targetUrlField}
          tabIndex={0}
          {...ariaLabelProps}
        >
          <Image field={fields.Image} />
        </Link>
      ) : (
        <Image field={fields.Image} />
      )}
      {fields.ImageCaption && (
        <Text
          tag="span"
          className="block italic text-dark-gray"
          field={fields.ImageCaption}
        />
      )}
    </div>
  );
};

export const Banner = (props: ImageProps): JSX.Element => {
  console.log("---->[Image] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields?.Image) {
    return <NoDataFallback componentName="Image" />;
  }

  const id = params?.RenderingIdentifier;
  const styles = params?.Styles ?? "";

  const isHeroBannerEmpty =
    isPageEditing && fields.Image?.value?.class === "scEmptyImage";
  const heroBannerEmptyClass = isHeroBannerEmpty ? "hero-banner-empty" : "";

  const backgroundStyle: CSSProperties = fields.Image?.value?.src
    ? { backgroundImage: `url('${fields.Image.value.src}')` }
    : {};

  // In editing mode the field is rendered (stretched to fill the banner) so
  // the image can be edited inline in Pages. Outside of editing the image
  // only contributes its src to the background-image style.
  const editingImageField = {
    ...fields.Image,
    value: {
      ...fields.Image.value,
      style: { width: "100%", height: "100%" },
    },
  };

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`relative p-0 lg:px-2 ${styles} ${heroBannerEmptyClass} ${hiddenBlockStyles}`}
      id={id || undefined}
      data-role="banner-component"
    >
      <div
        className="bg-cover bg-no-repeat bg-center lg:h-[800px] h-[300px]"
        style={backgroundStyle}
      >
        {isPageEditing && (
          <Image field={editingImageField} imageParams={{ mw: 1200 }} />
        )}
      </div>
    </div>
  );
};
